import React, { useEffect, useRef, useState } from 'react'
import classNames from 'classnames'

// components
import { BUTTON_THEME, Icon, CircularLoader as Loader } from '@st-operations/uikit/src/v2/index.js'
import Header from '_components/header'
import MoreInfoDropdown from '_components/more-info-dropdown'
import ListView from '_components/list-view'
import BatchSidebar, { ACTIONS } from '_components/batch-sidebar'
import { CHECKBOX_ACTION } from '_components/table'
import CreateTaxonomyAudienceForm from '_components/taxonomy/audiences/create'
import ItemInfoCell from '_components/item-info-cell'

// lib
import bulkUpdate from '_lib/bulkUpdate'
import pretty from '@st-operations/uikit/lib/pretty.js'
import { logErrorAsync } from '_lib/utility'
import User from '_lib/User'

// kit
import Events from '@st-operations/uikit/Events'
import Storage from '@st-operations/uikit/Storage'

// services
import { get } from '_services/requests'
import { approveAllAudiences } from '_services/taxonomies'

// Styles
import './styles.scss'

const formatSizeCount = (size) => {
  if (!size) return '-'
  else if (size >= 1000000000) {
    return `${Math.floor(10 * (size / 1000000000)) / 10}b`
  } else if (size >= 1000000) {
    return `${Math.floor(10 * (size / 1000000)) / 10}m`
  } else if (size >= 1000) {
    return `${Math.floor(size / 1000)}k`
  }
  return `${size}`
}
const renderStatus = (status) => {
  const classes = classNames('rounded-md p-1 px-2 w-fit font-semibold text-sm', {
    'bg-primary text-gray-100': status === 'processed',
    'bg-secondary-100 text-primary': status === 'approved' || status === 'submitted',
    'bg-[transparent] text-gray-400 border border-solid border-gray-400': status === 'draft',
    'bg-[transparent] text-red-400 border border-solid border-red-400':
      status === 'archived' || status === 'expired',
  })
  return (
    <div style={{ width: '100% !important' }}>
      <span className={classes}>{pretty(status)}</span>
    </div>
  )
}

const AUDIENCE_STATUSES = [
  { label: 'Draft', value: 'draft' },
  { label: 'Submitted', value: 'submitted' },
  { label: 'Processed', value: 'processed' },
  { label: 'Approved', value: 'approved' },
  { label: 'Archived', value: 'archived' },
  { label: 'Expired', value: 'expired' },
]

const TaxonomyAudiences = (props) => {
  const { slug } = props.match.params
  const [state, _setState] = useState({
    remountKey: 1,
    batch_percentage: 0,
    batch_size: 0,
    error: null,
    show_info: false,
    show_create_page: false,
    segmentDestinations: [],
    taxonomy: {
      name: 'Taxonomies',
    },
  })

  const setState = (newState) => _setState((prevState) => ({ ...prevState, ...newState }))
  const listViewItemsRef = useRef([])

  const softRefresh = (error) => !error && setState({ remountKey: Math.random() })

  const startBatchProcess = (ids) => {
    Storage.set('abort_batch', false)
    Events.emit('set_batch_process', true)
    setState({ batch_percentage: 0, batch_size: ids.length ?? ids.size })
  }

  const updateBatchProgress = () => {
    _setState((prevState) => ({
      ...prevState,
      batch_percentage: prevState.batch_percentage + 1 / prevState.batch_size,
    }))
  }

  const toggleCreateAudienceForm = () => {
    setState({ show_create_page: !state.show_create_page })
  }

  const deleteAudience = (items) => {
    const ids = items.map(({ _id }) => _id)
    startBatchProcess(ids)
    let bulkAction = {
      body: {},
      ids,
      method: 'delete',
      url: '/dmp-audiences',
    }
    bulkUpdate(bulkAction, updateBatchProgress, softRefresh)
  }

  const handleApproveAllAudiences = () => {
    if (!state.taxonomy._id) return null
    approveAllAudiences(state.taxonomy._id)
      .then((response) => {
        toast.success('All audiences approved')
        setState({ remountKey: Math.random() })
      })
      .catch((error) => {
        toast.error('Error approving all audiences')
      })
  }

  const getTaxonomyTitle = (slug) => {
    get(
      ['taxonomies', ''],
      {},
      {
        fields: ['name', 'slug', '_id'],
        slug,
      }
    ).then(({ count, items }) => {
      if (count > 0) {
        const { _id, name, slug } = items[0]
        setState({
          taxonomy: {
            _id,
            name,
            slug,
          },
        })
      }

      setState({ loading: false })
    })
  }

  const loadSegmentDestinations = () => {
    window._dmp_api.get(`/v1.0/segment-destinations`, (error, data) => {
      if (data) {
        let segment_destinations = data.segment_destinations.map((des) => ({
          value: des.name,
          label: des.name,
        }))
        setState({ segment_destinations })
      }
      if (error) {
        logErrorAsync(error)
      }
    })
  }

  useEffect(() => {
    User.watch(this, (user) => {
      if (user) {
        getTaxonomyTitle(slug)
        loadSegmentDestinations()
      }
    })
  }, [slug])

  const handleBatchSidebarAction = (action, items) => {
    switch (action) {
      case ACTIONS.DELETE:
        return deleteAudience(items)
    }
  }

  const handleCloseInfo = () => {
    setState({ show_info: false })
  }

  const handleExit = () => {
    props.history.push('/taxonomies')
  }

  const handleSelectionChange = (payload) => {
    const { action, items } = payload
    // "select all pages" already carries the resolved entities and total count
    if (action === CHECKBOX_ACTION.SELECT_ALL_PAGES) {
      Events.emit('ListView-selection', payload)
      return
    }
    const selectedEntities = listViewItemsRef.current.filter(({ _id }) => items.includes(_id))
    Events.emit('ListView-selection', { action, items, entities: selectedEntities })
  }

  const getSizeStr = (audience) => {
    const max_size = Math.max(
      ...Array.from(
        (() => {
          const result = []
          const audienceSizeKeys = audience?.sizes ? Object.keys(audience.sizes) : []
          for (const k of audienceSizeKeys) {
            const v = audience.sizes[k]
            result.push(v)
          }
          return result
        })() || []
      )
    )
    if (audience.sizes?.global) {
      return `global: ${formatSizeCount(audience.sizes?.global)}`
    } else if (audience.sizes?.us) {
      return `us: ${formatSizeCount(audience.sizes?.us)}`
    } else if (max_size > 0) {
      return formatSizeCount(max_size)
    }
    return `est: ${formatSizeCount(audience.est_size)}`
  }

  const renderPage = () => {
    const {
      error,
      loading,
      show_create_page,
      show_info,
      segmentDestinations,
      taxonomy,
      remountKey,
    } = state

    if (error) {
      return <p>{error.message}</p>
    }

    if (loading) {
      return <Loader className="audience-creator__loader" />
    }

    if (show_create_page) {
      return (
        <CreateTaxonomyAudienceForm
          segmentDestinations={segmentDestinations}
          taxonomy={taxonomy}
          onToggle={toggleCreateAudienceForm}
        />
      )
    }

    return (
      <>
        <Header
          title={`${taxonomy.name} > Manage Audiences`}
          actions={[
            { children: 'Exit', onClick: handleExit, theme: BUTTON_THEME.GHOST },
            {
              children: 'Approve All',
              onClick: handleApproveAllAudiences,
              theme: BUTTON_THEME.SECONDARY,
            },
            { children: 'Create New Audience', onClick: toggleCreateAudienceForm },
            {
              theme: BUTTON_THEME.GHOST,
              children: (
                <Icon
                  type="outline"
                  icon="info"
                  color="#13682B"
                  className="header__button"
                  width="24px"
                  height="24px"
                />
              ),
              onClick: () => {
                _setState((prevState) => ({ ...prevState, show_info: !prevState.show_info }))
              },
            },
          ]}
        />
        <If condition={show_info}>
          <MoreInfoDropdown onClose={handleCloseInfo} />
        </If>
        <div className="batch-sidebar__list-view-wrapper">
          <ListView
            key={remountKey}
            className="st-list-view--has-sidebar"
            showCheckboxes={true}
            allowSelectAllPages={true}
            onSelectionChange={handleSelectionChange}
            batch_percentage={state.batch_percentage}
            batch_size={state.batch_size}
            itemsRef={listViewItemsRef}
            columns={[
              {
                key: 'name',
                name: 'Audience Name',
                link: `/audience/:_id`,
                sortable: true,
                width: 400,
                render: (item) => (
                  <ItemInfoCell item={item} key="name" link={`/audience/${item._id}`} />
                ),
              },
              {
                name: 'Size',
                key: 'size',
                styles: {
                  textAlign: 'center',
                },
                render: (item) => {
                  return getSizeStr(item)
                },
              },
              {
                name: 'Updated at',
                key: 'updated_at',
                sortable: true,
                styles: {
                  textAlign: 'center',
                },
              },
              {
                key: 'status',
                name: 'Status',
                styles: {
                  width: '200px',
                  textAlign: 'center',
                },
                render: (item) => {
                  return renderStatus(item.status)
                },
              },
            ]}
            customPlaceholder="Search for Audience"
            entities={`taxonomies/${slug}/audiences`}
            filters={[
              {
                name: 'status',
                label: 'Audience Status',
                options: AUDIENCE_STATUSES,
                all: true,
              },
            ]}
            sort_key={'name'}
            sort_dir={1}
          />
          <BatchSidebar
            onConfirm={handleBatchSidebarAction}
            actions={[ACTIONS.DELETE]}
            entityLabel="audiences"
          />
        </div>
      </>
    )
  }

  return <div id="taxonomy-audiences">{renderPage()}</div>
}

export default TaxonomyAudiences
